import { DiagnosticsFooter, EmptyState, usePayload } from './primitives';
import styles from './styles.module.css';

const SCOPE_KEYS  = ['primary_market_theme', 'secondary_market_themes', 'dominant_market_themes', 'market_keyword_scope'];
const SIGNAL_KEYS = ['audience_keyword_signals', 'use_case_keyword_signals', 'niche_keyword_signals'];
const SOURCE_KEYS = ['shopify_product_language', 'shopify_collection_language', 'public_market_language', 'target_market_keywords'];
const ALIGN_KEYS  = ['market_alignment_score', 'missing_market_terms', 'market_keyword_source_strength', 'keyword_confidence_level'];

const ChipList = ({ values, primary = false }) => {
  if (!Array.isArray(values) || !values.length) return <span className={styles.cardValueEmpty}>—</span>;
  return (
    <ul className={styles.chipList} style={{ marginTop: 2 }}>
      {values.map((v, i) => (
        <li key={i} className={`${styles.chip} ${primary ? styles.chipPrimary : ''}`}>{v}</li>
      ))}
    </ul>
  );
};

const FieldCard = ({ field }) => {
  if (!field) return null;
  const val = field.value;
  const isArr = Array.isArray(val);
  const isObj = val && typeof val === 'object' && !isArr;
  const isEmpty = val === null || val === undefined || val === '' || (isArr && !val.length);
  const evidence = Array.isArray(field.evidence) && field.evidence.length ? field.evidence : null;

  return (
    <article className={`${styles.card} ${isEmpty ? styles.cardEmpty : ''}`}>
      <header className={styles.cardHeader}>
        <p className={styles.cardLabel}>{field.label}</p>
      </header>
      {isEmpty ? (
        <p className={styles.cardValueEmpty}>—</p>
      ) : isObj ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 2 }}>
          {Object.entries(val).filter(([, v]) => v && (Array.isArray(v) ? v.length : true)).map(([k, v]) => (
            <div key={k} style={{ fontSize: 11, color: 'var(--tp)' }}>
              <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{k.replace(/_/g, ' ')}: </span>
              {Array.isArray(v) ? v.join(', ') : String(v)}
            </div>
          ))}
        </div>
      ) : isArr ? (
        <ChipList values={val} />
      ) : (
        <p className={styles.cardValue}>{String(val)}</p>
      )}
      {evidence && (
        <ul className={styles.evidenceList}>
          {evidence.map((e, i) => <li key={i} className={styles.evidenceItem}>{e}</li>)}
        </ul>
      )}
    </article>
  );
};

const KeywordsViewer = ({ report }) => {
  const payload = usePayload(report);
  if (!report) return <EmptyState />;

  const fields = Array.isArray(payload?.fields) ? payload.fields : [];
  const byKey  = Object.fromEntries(fields.map((f) => [f.key, f]));

  const get = (keys) => keys.map((k) => byKey[k]).filter(Boolean);

  const scopeFields  = get(SCOPE_KEYS);
  const signalFields = get(SIGNAL_KEYS);
  const sourceFields = get(SOURCE_KEYS);
  const alignFields  = get(ALIGN_KEYS);

  const finalSet  = byKey['final_market_keyword_set'];
  const kwCount   = byKey['number_of_market_keywords'];
  const confidence = payload?.diagnostics?.profile_confidence_level || null;
  const missing    = Array.isArray(payload?.diagnostics?.missing_data_flags) ? payload.diagnostics.missing_data_flags : [];

  return (
    <div className={styles.viewer}>

      {payload?.summary && (
        <section className={styles.summaryCard}>
          <p className={styles.sectionLabel}>Summary</p>
          <p className={styles.summaryText}>{payload.summary}</p>
          {confidence && (
            <div className={styles.summaryFooter}>
              <span className={styles.metaLabel}>Keyword confidence</span>
              <span className={`${styles.badge} ${styles[`badge${confidence}`]}`}>{confidence}</span>
            </div>
          )}
        </section>
      )}

      {scopeFields.length > 0 && (
        <section>
          <p className={styles.sectionLabel}>Market themes</p>
          <div className={styles.grid}>
            {scopeFields.map((f) => <FieldCard key={f.key} field={f} />)}
          </div>
        </section>
      )}

      {signalFields.length > 0 && (
        <section>
          <p className={styles.sectionLabel}>Keyword signals</p>
          <div className={styles.grid}>
            {signalFields.map((f) => <FieldCard key={f.key} field={f} />)}
          </div>
        </section>
      )}

      {finalSet && (
        <section className={styles.signatureCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <p className={styles.sectionLabel} style={{ margin: 0 }}>Final market keyword set</p>
            {kwCount?.value !== undefined && (
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--tp)', background: 'var(--bg-app)', border: '1px solid var(--bd)', borderRadius: 100, padding: '2px 9px' }}>
                {kwCount.value} phrases
              </span>
            )}
          </div>
          <ChipList values={finalSet.value} />
        </section>
      )}

      {sourceFields.length > 0 && (
        <section>
          <p className={styles.sectionLabel}>Source breakdown</p>
          <div className={styles.grid}>
            {sourceFields.map((f) => <FieldCard key={f.key} field={f} />)}
          </div>
        </section>
      )}

      {alignFields.length > 0 && (
        <section>
          <p className={styles.sectionLabel}>Alignment & quality</p>
          <div className={styles.grid}>
            {alignFields.map((f) => <FieldCard key={f.key} field={f} />)}
          </div>
        </section>
      )}

      <DiagnosticsFooter missingFlags={missing} />
    </div>
  );
};

export default KeywordsViewer;
